import React, { useState, useEffect, useCallback } from "react";
import { displayName, useMaskNames } from "./nameMask";
import { RemapDialog } from "./PmrMatchLines";

/**
 * Unmatched PMR products - read only review dialog.
 *
 * Everything a PMR upload could not map onto dm+d, biggest hole first. The
 * default view is one row per PRODUCT across the whole org rather than one
 * row per branch, because a product queued at fifteen branches is still one
 * mapping decision, and the per-branch list makes the queue look five times
 * worse than it is.
 *
 * Nothing here writes. Expand a row to see the candidates the matcher found
 * and which branches are affected.
 */

const API = "https://api.pharmacegroup.co.uk";

/* Appliance classification lives on the server (pmr_classify.js) so the tab
 * counts, the paging and the tint all agree. Rows arrive with an `appliance`
 * flag; this file only decides what colour that is. */
const APPLIANCE_BG = "#fdf3e3";

const BAND_STYLE = {
  // "suggested" is the band that exists because no threshold could separate
  // the couple of dozen correct leftovers from the four wrong ones. Nothing
  // is written for it; it waits here for a decision.
  suggested: { background: "#e6f4ea", color: "#1e5b30", border: "1px solid #a8d5b8" },
  review:  { background: "#fff4d6", color: "#7a5b00", border: "1px solid #e6cd8a" },
  weak:    { background: "#ffe3e3", color: "#8a2020", border: "1px solid #e8a9a9" },
  none:    { background: "#eee",    color: "#555",    border: "1px solid #ccc" },
  untried: { background: "#eef3ff", color: "#2a4a8a", border: "1px solid #b9c9ee" },
};

const Badge = ({ band }) => (
  <span style={{
    ...(BAND_STYLE[band] || BAND_STYLE.none),
    padding: "1px 7px", borderRadius: "10px", fontSize: "0.78em", whiteSpace: "nowrap",
  }}>
    {band}
  </span>
);

const PmrUnmatched = ({ groupId, members = [], onClose }) => {
  const masked = useMaskNames();

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [groupBy, setGroupBy] = useState("product");
  const [band, setBand] = useState("");
  const [pharmacyId, setPharmacyId] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("units");
  const [skip, setSkip] = useState(0);
  const [expanded, setExpanded] = useState({});
  // which tab: medicines and feeds, or appliances and dressings
  const [category, setCategory] = useState("medicine");
  const [picking, setPicking] = useState(null);
  const [busy, setBusy] = useState(null);

  const LIMIT = 200;

  const nameFor = (id) => {
    const m = members.find((x) => x.pharmacyId === id);
    return m ? displayName(m.pharmacyName, masked) : (id || "").slice(0, 8);
  };

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API}/pmrUnmatched`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          groupId, groupBy, sort, skip, limit: LIMIT,
          band: band || undefined,
          category,
          pharmacyId: pharmacyId || undefined,
          search: search.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Could not load"); return; }
      setRows(data.rows || []);
      setTotals(data.totals || null);
    } catch (e) {
      setError("Could not reach the server");
    } finally {
      setLoading(false);
    }
  }, [groupId, groupBy, sort, skip, band, category, pharmacyId, search]);

  useEffect(() => { load(); }, [load]);

  // any filter change goes back to the first page
  useEffect(() => { setSkip(0); }, [groupBy, band, category, pharmacyId, sort]);

  const act = async (row, path, body, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(row.key);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ groupId, key: row.matchKey, ...body }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { alert(data.error || "That did not work"); return; }
      setPicking(null);
      await load();
    } catch (e) {
      alert("Could not reach the server");
    } finally {
      setBusy(null);
    }
  };

  const accept = (row, vppid, name) => act(
    row, "/pmrUnmatchedAccept", { vppid },
    `Count "${row.label}" as:\n\n${name}\n\n`
      + `${row.units.toLocaleString()} units across ${row.siteCount} `
      + `${row.siteCount === 1 ? "branch" : "branches"} will be written to usage, and `
      + `future uploads of this line will go there automatically.`,
  );

  const ignore = (row) => act(
    row, "/pmrUnmatchedIgnore", {},
    `Dismiss "${row.label}"?\n\n`
      + `Its ${row.units.toLocaleString()} units stay uncounted and it stops being offered.`,
  );

  const exportCsv = async () => {
    const token = localStorage.getItem("authToken");
    const res = await fetch(`${API}/pmrUnmatchedExport`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ groupId, band: band || undefined, category,
                             pharmacyId: pharmacyId || undefined }),
    });
    if (!res.ok) { alert("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pmr_unmatched.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const th = { textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #ddd",
               position: "sticky", top: 0, background: "#fafafa", zIndex: 1 };
  const td = { padding: "6px 8px", borderBottom: "1px solid #eee", verticalAlign: "top" };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
                  background: "rgba(0,0,0,0.5)", zIndex: 3000 }}>
      <div style={{ background: "white", margin: "3% auto", padding: "20px",
                    width: "1150px", maxWidth: "95%", maxHeight: "88%", overflow: "auto" }}>

        <div style={{ position: "sticky", top: 0, background: "white", paddingBottom: "10px",
                      marginBottom: "6px", borderBottom: "1px solid #eee", display: "flex",
                      justifyContent: "space-between", alignItems: "center", zIndex: 2 }}>
          <h2 style={{ margin: 0 }}>Unmatched PMR products</h2>
          <div>
            <button onClick={exportCsv} style={{ marginRight: "10px" }}>Export CSV</button>
            <button onClick={onClose}>Close</button>
          </div>
        </div>

        {/* The denominator, first. "821 not counted" reads very differently
            once you can see what did come through, and on its own it invites
            the wrong conclusion about the data as a whole. */}
        {totals?.matched?.products > 0 && (
          <div style={{ marginBottom: "4px", color: "#444" }}>
            <strong>{totals.matched.products.toLocaleString()}</strong> matched products,{" "}
            <strong>{totals.matched.rows.toLocaleString()}</strong> branch lines,{" "}
            <strong>{totals.matched.units.toLocaleString()}</strong> units of usage counted.
            {(totals.matched.units + totals.units) > 0 && (
              <span style={{ marginLeft: "8px", color: "#666" }}>
                {Math.round(100 * totals.matched.units
                            / (totals.matched.units + totals.units))}% of PMR usage by volume
              </span>
            )}
          </div>
        )}

        {totals && (
          <div style={{ marginBottom: "10px", color: "#444" }}>
            <strong>{totals.products.toLocaleString()}</strong> unmatched products,{" "}
            <strong>{totals.rows.toLocaleString()}</strong> branch lines,{" "}
            <strong>{totals.units.toLocaleString()}</strong> units of usage not counted.

            {/* Products, to match the figures on the line above. These used to
                show branch lines, which summed to 2,462 next to a headline of
                821 and read as a contradiction. */}
            <div style={{ marginTop: "3px", color: "#555" }}>
              How close the matcher got, by product:
              {Object.entries(totals.byBand).map(([b, v]) => (
                <span key={b} style={{ marginLeft: "8px" }}>
                  <Badge band={b} />{" "}
                  {(v.products ?? v.rows).toLocaleString()}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Two different jobs, so two tabs. Appliances are roughly half the
            products and a fraction of a percent of the units, and their dm+d
            targets are broad classes rather than products - mixing them into
            the medicines list buries the feeds and drugs that carry the
            volume. */}
        {totals && (
          <div style={{ display: "flex", gap: "4px", marginBottom: "12px" }}>
            {[
              { id: "medicine", label: "Medicines and feeds", t: totals.medicine },
              { id: "appliance", label: "Appliances, dressings and stoma", t: totals.appliance },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setCategory(tab.id)}
                style={{
                  padding: "7px 14px",
                  fontWeight: category === tab.id ? 700 : 400,
                  background: category === tab.id
                    ? (tab.id === "appliance" ? APPLIANCE_BG : "#e8effb")
                    : "#f4f4f4",
                  border: category === tab.id ? "1px solid #8aa4d0" : "1px solid #ddd",
                  borderBottom: category === tab.id ? "2px solid #2a4a8a" : "1px solid #ddd",
                  cursor: "pointer",
                }}
              >
                {tab.label}
                <span style={{ color: "#666", fontWeight: 400 }}>
                  {" "}({(tab.t?.products ?? 0).toLocaleString()} products,{" "}
                  {(tab.t?.units ?? 0).toLocaleString()} units)
                </span>
              </button>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap",
                      marginBottom: "12px" }}>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            <option value="product">One row per product</option>
            <option value="site">One row per branch</option>
          </select>

          <select value={band} onChange={(e) => setBand(e.target.value)}>
            <option value="">All confidence bands</option>
            <option value="suggested">suggested</option>
            <option value="review">review</option>
            <option value="weak">weak</option>
            <option value="none">none</option>
          </select>

          <select value={pharmacyId} onChange={(e) => setPharmacyId(e.target.value)}>
            <option value="">All branches</option>
            {members.map((m) => (
              <option key={m.pharmacyId} value={m.pharmacyId}>
                {displayName(m.pharmacyName, masked)}
              </option>
            ))}
          </select>

          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="units">Most usage first</option>
            <option value="score">Best candidate first</option>
            <option value="label">Name</option>
          </select>

          <input
            placeholder="Filter by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setSkip(0); load(); } }}
            style={{ minWidth: "220px" }}
          />
          <button onClick={() => { setSkip(0); load(); }}>Search</button>
        </div>

        {error && <div style={{ color: "#a00", marginBottom: "10px" }}>{error}</div>}
        {loading && <div style={{ marginBottom: "10px" }}>Loading...</div>}

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9em" }}>
          <thead>
            <tr>
              <th style={{ ...th, width: "30px" }}></th>
              <th style={th}>PMR product</th>
              <th style={{ ...th, width: "60px", textAlign: "right" }}>Pack</th>
              <th style={{ ...th, width: "80px", textAlign: "right" }}>Units</th>
              <th style={{ ...th, width: "70px", textAlign: "right" }}>Branches</th>
              <th style={{ ...th, width: "80px" }}>Band</th>
              <th style={th}>Best candidate</th>
              <th style={{ ...th, width: "110px", textAlign: "right" }}>Score / margin</th>
              <th style={{ ...th, width: "190px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const top = (r.suggestions || [])[0];
              const open = !!expanded[r.key];
              return (
                <React.Fragment key={r.key}>
                  <tr
                    onClick={() => setExpanded((p) => ({ ...p, [r.key]: !p[r.key] }))}
                    style={{ cursor: "pointer",
                             background: open ? "#f6f9ff"
                                       : r.appliance ? APPLIANCE_BG : undefined }}
                  >
                    <td style={td}>{open ? "\u25be" : "\u25b8"}</td>
                    <td style={td}>{r.label}</td>
                    <td style={{ ...td, textAlign: "right" }}>{r.packSize}{r.packUnit}</td>
                    <td style={{ ...td, textAlign: "right" }}>{r.units.toLocaleString()}</td>
                    <td style={{ ...td, textAlign: "right" }}>{r.siteCount}</td>
                    <td style={td}><Badge band={r.band} /></td>
                    <td style={{ ...td, color: top ? "#222" : "#999" }}>
                      {top ? top.vmppNm : "no candidate found"}
                      {top && top.qtval != null && (
                        <span style={{ color: "#777" }}> [pack {top.qtval}]</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                      {top ? `${top.score} / ${r.margin}` : "-"}
                    </td>
                    <td style={td} onClick={(e) => e.stopPropagation()}>
                      {r.matchKey ? (
                        <span style={{ whiteSpace: "nowrap" }}>
                          {top && (
                            <button
                              disabled={busy === r.key}
                              onClick={() => accept(r, top.vppid, top.vmppNm)}
                              style={{ marginRight: "4px" }}
                              title="Write this match into usage"
                            >
                              Accept
                            </button>
                          )}
                          <button
                            disabled={busy === r.key}
                            onClick={() => setPicking(r)}
                            style={{ marginRight: "4px" }}
                            title="Pick a different dm+d product for this line"
                          >
                            Allocate
                          </button>
                          <button disabled={busy === r.key} onClick={() => ignore(r)}>
                            Ignore
                          </button>
                        </span>
                      ) : (
                        <span style={{ color: "#999" }}>legacy row</span>
                      )}
                    </td>
                  </tr>

                  {open && (
                    <tr>
                      <td></td>
                      <td colSpan={8} style={{ ...td, background: "#fbfcff" }}>
                        <div style={{ marginBottom: "8px" }}>
                          <strong>Candidates</strong>
                          {!r.suggestions?.length && (
                            <div style={{ color: "#999" }}>
                              The matcher found nothing for this line.
                            </div>
                          )}
                          {(r.suggestions || []).map((s, i) => (
                            <div key={s.vppid || i} style={{ padding: "2px 0" }}>
                              <span style={{ display: "inline-block", width: "44px",
                                             fontWeight: i === 0 ? 600 : 400 }}>
                                {s.score}
                              </span>
                              <span style={{ fontWeight: i === 0 ? 600 : 400 }}>{s.vmppNm}</span>
                              {s.qtval != null && (
                                <span style={{ color: "#777" }}> [pack {s.qtval}]</span>
                              )}
                              {r.matchKey && (
                                <button
                                  disabled={busy === r.key}
                                  onClick={() => accept(r, s.vppid, s.vmppNm)}
                                  style={{ marginLeft: "8px" }}
                                >
                                  Use this
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        {r.suppliers?.length > 0 && (
                          <div style={{ marginBottom: "8px", color: "#555" }}>
                            Suppliers seen: {r.suppliers.join(", ")}
                          </div>
                        )}

                        <div>
                          <strong>Branches</strong>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px",
                                        marginTop: "4px" }}>
                            {(r.sites || []).map((s, i) => (
                              <span key={`${s.pharmacyId}-${i}`}
                                    style={{ padding: "2px 8px", background: "#eef1f6",
                                             borderRadius: "10px" }}>
                                {nameFor(s.pharmacyId)}: {s.units.toLocaleString()}
                              </span>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} style={{ ...td, textAlign: "center", color: "#777",
                                         padding: "24px" }}>
                  Nothing unmatched for these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {picking && (
          <RemapDialog
            line={{ title: "Allocate this line to the right dm+d product",
                    label: picking.label, packSize: picking.packSize, units: picking.units }}
            onCancel={() => setPicking(null)}
            onPick={(c) => accept(picking, c.vppid, c.vmppNm)}
          />
        )}

        <div style={{ marginTop: "12px", display: "flex", gap: "10px", alignItems: "center" }}>
          <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))}>
            Previous
          </button>
          <span style={{ color: "#666" }}>
            {rows.length ? `${skip + 1} to ${skip + rows.length}` : "0"}
          </span>
          <button disabled={rows.length < LIMIT} onClick={() => setSkip(skip + LIMIT)}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PmrUnmatched;
